import { useState } from 'react';
import { ApiError } from '../api/client';
import { useVillageIntel, useWorldOptions } from '../api/queries';
import {
  ATTACK_SIZE_ICON,
  ATTACK_SIZE_LABEL,
  VILLAGE_INTEL_EVIDENCE_LABEL,
  VILLAGE_ROLE_LABEL,
  type User,
  type VillageIntel,
  type VillageRole,
} from '../api/types';
import { Spinner } from '../components/Spinner';
import { useDebounced } from '../hooks/useDebounced';
import { formatCoords, formatDate } from '../lib/reportFormat';

const ROLE_BADGE_CLASS: Record<VillageRole, string> = {
  off: 'badge-danger',
  deff: 'badge-active',
  off_deff: 'badge-pending',
  unknown: 'badge-disabled',
};

/** Belege eines Dorfs, als Zeilen für den Tooltip (title-Attribut, kein eigenes Popover nötig). */
function evidenceTooltip(village: VillageIntel): string {
  return village.evidence
    .map((entry) => `${VILLAGE_INTEL_EVIDENCE_LABEL[entry.kind]} (${entry.bp} BP) — ${formatDate(entry.battleAt)}`)
    .join('\n');
}

/** Kurzform je Einheitentyp, wie in der Berichtsansicht. */
const OFF_UNIT_LABELS = { axe: 'Axt', light: 'LKav', ram: 'Ramme' } as const;

/** Rundet auf volle Zehner -- eine Hochrechnung ist ohnehin nur eine Schätzung, falsche Präzision wäre irreführend. */
function roundTroopCount(value: number | undefined): number {
  return Math.round((value ?? 0) / 10) * 10;
}

/** Hover-Text des Angriffsgrößen-Icons: die tatsächliche Truppenhochrechnung. */
function troopProjectionTooltip(projection: NonNullable<VillageIntel['troopProjection']>): string {
  const troops = (['axe', 'light', 'ram'] as const)
    .map((unit) => `~${roundTroopCount(projection.projected[unit])} ${OFF_UNIT_LABELS[unit]}`)
    .join(' / ');
  const label =
    projection.basis === 'evidence' ? `Mögliche Truppen: ${troops}` : `Keine Truppendaten — volle Off möglich: ${troops}`;
  return `${ATTACK_SIZE_LABEL[projection.attackSize]}\n${label}`;
}

/**
 * "Off-/Deff-Dörfer": aus vorhandenen Berichten hergeleitete Vermutung, ob
 * ein gegnerisches Dorf ein Offensiv- oder Defensiv-Dorf ist -- rein
 * heuristisch, kein Ersatz für eigenes Urteilsvermögen. Die Einordnung wird
 * beim Einlesen eines Berichts fortgeschrieben und bleibt bestehen, bis ein
 * neuer Bericht etwas anderes belegt (z. B. einen neuen Besitzer).
 */
export function VillageIntelPage({ user }: { user: User }) {
  // Leer gelassen: kein Zeitraum (alle gespeicherten Dörfer), bzw. der Standard-
  // Zeitraum der Welteinstellung -- der als Platzhalter im Feld erscheint.
  const [days, setDays] = useState('');
  const debouncedDays = useDebounced(days);
  const parsedDays = debouncedDays.trim() ? Number(debouncedDays) : null;
  const daysValid = parsedDays === null || (Number.isInteger(parsedDays) && parsedDays >= 1 && parsedDays <= 90);

  const needsWorldChoice = user.world === null;
  const [worldKey, setWorldKey] = useState('');
  const worlds = useWorldOptions('login');
  const activeWorld = user.world?.key ?? worldKey;

  const intel = useVillageIntel({
    welt: needsWorldChoice ? worldKey : undefined,
    ...(daysValid && parsedDays !== null ? { tage: parsedDays } : {}),
  });

  const rows = intel.data?.villages ?? [];

  return (
    <section className="card">
      <div className="card-head">
        <div>
          <h1>Off-/Deff-Dörfer</h1>
          <p className="muted">
            Aus vorhandenen Berichten hergeleitete Vermutung, ob ein gegnerisches Dorf ein
            Offensiv- oder Defensiv-Dorf ist — eine Vorsortierung, kein Ersatz für eigenes
            Urteilsvermögen. Die Einordnung bleibt bestehen, bis ein neuer Bericht etwas anderes belegt.
          </p>
        </div>
      </div>

      {needsWorldChoice && (
        <label className="field">
          <span>Welt</span>
          <select value={worldKey} onChange={(event) => setWorldKey(event.target.value)} required>
            <option value="" disabled>
              Welt wählen …
            </option>
            {(worlds.data?.worlds ?? []).map((world) => (
              <option key={world.key} value={world.key}>
                {world.name}
              </option>
            ))}
          </select>
        </label>
      )}

      {needsWorldChoice && !worldKey && (
        <p className="muted center pad">Wähle eine Welt, um ihre Off-/Deff-Dörfer zu sehen.</p>
      )}

      {activeWorld && (
        <>
          <div className="filter-bar">
            <label className="field" style={{ maxWidth: '160px' }}>
              <span>Zeitraum (Tage)</span>
              <input
                type="number"
                min={1}
                max={90}
                value={days}
                placeholder={intel.data?.days != null ? String(intel.data.days) : 'alle'}
                onChange={(event) => setDays(event.target.value)}
              />
            </label>
          </div>

          {!daysValid && (
            <div className="flash flash-error">Zeitraum muss zwischen 1 und 90 Tagen liegen.</div>
          )}

          {intel.isPending && <Spinner label="Berichte werden ausgewertet …" />}

          {intel.isError && (
            <div className="flash flash-error">
              {intel.error instanceof ApiError ? intel.error.message : 'Auswertung fehlgeschlagen.'}
            </div>
          )}

          {!intel.isPending && rows.length === 0 && (
            <p className="muted center pad">
              Keine Off-/Deff-Dörfer erkannt. Entweder liegen noch nicht genug Berichte vor,
              oder der gewählte Zeitraum ist zu kurz.
            </p>
          )}

          {rows.length > 0 && (
            <div className={`table-wrap ${intel.isFetching ? 'is-refreshing' : ''}`}>
              <table className="table">
                <thead>
                  <tr>
                    <th>Rolle</th>
                    <th>Größe</th>
                    <th>Dorf</th>
                    <th>Koordinaten</th>
                    <th>Spieler</th>
                    <th>Zuletzt gesehen</th>
                    <th>Belege</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((village) => (
                    <tr key={village.villageId}>
                      <td>
                        <span className={`badge ${ROLE_BADGE_CLASS[village.role]}`}>
                          {VILLAGE_ROLE_LABEL[village.role]}
                        </span>
                      </td>
                      <td className="center">
                        {village.troopProjection && (
                          <img
                            src={ATTACK_SIZE_ICON[village.troopProjection.attackSize]}
                            alt={ATTACK_SIZE_LABEL[village.troopProjection.attackSize]}
                            title={troopProjectionTooltip(village.troopProjection)}
                            className="report-icon"
                          />
                        )}
                      </td>
                      <td>{village.village ?? 'Unbekannt'}</td>
                      <td className="nowrap">{formatCoords(village)}</td>
                      <td>{village.playerName ?? 'Unbekannt'}</td>
                      <td className="nowrap">{formatDate(village.latestBattleAt)}</td>
                      <td className="nowrap" title={evidenceTooltip(village)}>
                        {village.evidence.length}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </section>
  );
}
